import React from "react";
import { Typography, TextField, Button, Box, CircularProgress, MenuItem, Select, FormControl, InputLabel, Divider, IconButton, Tooltip, Tabs, Tab } from "@material-ui/core";
import { GetApp, Fullscreen, FullscreenExit, ArrowForward, ArrowUpward, ArrowDownward, ExpandMore, ExpandLess } from "@material-ui/icons";
import { css } from "@emotion/css";
import { useHistory } from "react-router-dom";
import { useQuestion, useQuestions, useQuestionGroups, useBook, useBooks, useQuestionGroup, useUpdateQuestion, useUpdateBook, useUpdateQuestionGroup, useDeleteQuestion, useDeleteBook, useDeleteQuestionGroup } from "../queries";
import { healthFormsApi } from "../api";
import { QuestionRef, LocalizedText } from "../types/common";
import { Question, QuestionResponseType } from "../types/common";
import { Book, QuestionGroup, Part, Page } from "../types/persisted";
import TranslatedTextField from "./TranslatedTextField";
import { BookSchema as ResolvedBookSchema, QuestionGroupSchema as ResolvedQuestionGroupSchema } from "../types/resolved";
import type { Selection } from "./Sidebar";
import { processVisibleWhenForExport, processRequirementForExport, type InitialValues } from "./initialValuesUtils";

const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

type UsageItem = {
  id: number;
  name: string;
  type: "book" | "questionGroup";
  isDirect: boolean;
};

const findBooksAndGroupsUsingQuestion = async (
  questionId: number,
  books: Array<{ id: number; name: string }>,
  questionGroups: Array<{ id: number; name: string }>,
  allBooksData: Record<number, Book>
): Promise<UsageItem[]> => {
  const usages: UsageItem[] = [];

  const questionGroupsData: Record<number, QuestionGroup> = {};
  await Promise.all(
    questionGroups.map(async (qg) => {
      try {
        const data = await healthFormsApi.questionGroups.get(qg.id);
        questionGroupsData[qg.id] = data;
      } catch (err) {
        console.error(`Failed to fetch question group ${qg.id}:`, err);
      }
    })
  );

  const questionGroupsUsingQuestion: number[] = [];
  Object.values(questionGroupsData).forEach((qg) => {
    if (qg.questions.some((qRef) => qRef.questionId === questionId)) {
      questionGroupsUsingQuestion.push(qg.id);
      usages.push({
        id: qg.id,
        name: qg.name,
        type: "questionGroup",
        isDirect: true,
      });
    }
  });

  books.forEach((book) => {
    const bookData = allBooksData[book.id];
    if (!bookData) return;

    let isDirect = false;
    let isIndirect = false;

    for (const page of bookData.pages) {
      for (const part of page.parts) {
        if (part._type === "question" && part.questionId === questionId) {
          isDirect = true;
        }
        if (part._type === "question_group" && questionGroupsUsingQuestion.includes(part.questionGroup)) {
          isIndirect = true;
        }
      }
    }

    if (isDirect || isIndirect) {
      usages.push({
        id: book.id,
        name: book.name,
        type: "book",
        isDirect,
      });
    }
  });

  return usages;
};

const findBooksUsingQuestionGroup = (
  questionGroupId: number,
  books: Array<{ id: number; name: string }>,
  allBooksData: Record<number, Book>
): UsageItem[] => {
  const usages: UsageItem[] = [];

  books.forEach((book) => {
    const bookData = allBooksData[book.id];
    if (!bookData) return;

    for (const page of bookData.pages) {
      for (const part of page.parts) {
        if (part._type === "question_group" && part.questionGroup === questionGroupId) {
          usages.push({
            id: book.id,
            name: book.name,
            type: "book",
            isDirect: true,
          });
          return;
        }
      }
    }
  });

  return usages;
};

const resolveQuestionGroup = async (group: QuestionGroup, initialValues?: InitialValues): Promise<unknown> => {
  const resolvedQuestions = await Promise.all(
    group.questions.map(async (qRef) => {
      // If initial values are provided, process visibility and requirement
      if (initialValues) {
        const visibilityResult = processVisibleWhenForExport(qRef.visibility, initialValues);
        if (!visibilityResult.shouldInclude) {
          return null; // Exclude this question
        }

        const requirementResult = processRequirementForExport(qRef.requirement, initialValues);

        const question = await healthFormsApi.questions.get(qRef.questionId);
        return {
          ...qRef,
          visibility: visibilityResult.newVisibility,
          requirement: requirementResult.newRequirement,
          question,
        };
      } else {
        const question = await healthFormsApi.questions.get(qRef.questionId);
        return { ...qRef, question };
      }
    })
  );

  // Filter out null entries (questions that should be excluded)
  const filteredQuestions = resolvedQuestions.filter(q => q !== null);

  const resolved = { ...group, _type: "question_group" as const, questions: filteredQuestions };
  return ResolvedQuestionGroupSchema.parse(resolved);
};

const resolveBook = async (book: Book, initialValues?: InitialValues): Promise<unknown> => {
  const resolvedPages = await Promise.all(
    book.pages.map(async (page) => {
      const resolvedParts = await Promise.all(
        page.parts.map(async (part) => {
          if (part._type === "question") {
            // If initial values are provided, process visibility and requirement
            if (initialValues) {
              const visibilityResult = processVisibleWhenForExport(part.visibility, initialValues);
              if (!visibilityResult.shouldInclude) {
                return null; // Exclude this question
              }

              const requirementResult = processRequirementForExport(part.requirement, initialValues);

              const question = await healthFormsApi.questions.get(part.questionId);
              return {
                ...part,
                visibility: visibilityResult.newVisibility,
                requirement: requirementResult.newRequirement,
                question,
              };
            } else {
              const question = await healthFormsApi.questions.get(part.questionId);
              return { ...part, question };
            }
          }
          if (part._type === "question_group") {
            const group = await healthFormsApi.questionGroups.get(part.questionGroup);

            // If initial values provided, process the group's visibility
            if (initialValues) {
              const visibilityResult = processVisibleWhenForExport(part.visibility, initialValues);
              if (!visibilityResult.shouldInclude) {
                return null; // Exclude this question group
              }

              const resolvedGroup = await resolveQuestionGroup(group, initialValues);
              return { ...part, visibility: visibilityResult.newVisibility, questionGroup: resolvedGroup };
            } else {
              const resolvedGroup = await resolveQuestionGroup(group);
              return { ...part, questionGroup: resolvedGroup };
            }
          }
          return part;
        })
      );

      // Filter out null entries (parts that should be excluded)
      const filteredParts = resolvedParts.filter(p => p !== null);

      return { ...page, parts: filteredParts };
    })
  );
  const resolved = { ...book, _type: "book" as const, pages: resolvedPages };
  return ResolvedBookSchema.parse(resolved);
};

type EditorProps = {
  selection: Selection;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: (entity: Question | Book | QuestionGroup | null) => void;
};

const isValidSemanticKey = (key: string | undefined): boolean => {
  if (!key || key === "") return true; // Empty is valid (optional field)
  return /^[a-zA-Z][a-zA-Z0-9_]*$/.test(key);
};

const QuestionEditor: React.VFC<{ id: number; isExpanded: boolean; onToggleExpand: () => void; onEdit: (entity: Question | null) => void }> = ({ id, isExpanded, onToggleExpand, onEdit }) => {
  const history = useHistory();
  const { data: question, isLoading, error } = useQuestion(id);
  const booksQuery = useBooks();
  const questionGroupsQuery = useQuestionGroups();
  const updateQuestion = useUpdateQuestion();
  const deleteQuestion = useDeleteQuestion();
  const [editedQuestion, setEditedQuestion] = React.useState<Question | null>(null);
  const [allBooksData, setAllBooksData] = React.useState<Record<number, Book>>({});
  const [usages, setUsages] = React.useState<UsageItem[]>([]);
  const [isLoadingUsages, setIsLoadingUsages] = React.useState(false);
  const [editorMode, setEditorMode] = React.useState<"structured" | "ai">("structured");

  React.useEffect(() => {
    if (question) {
      setEditedQuestion(question);
    }
  }, [question]);

  React.useEffect(() => {
    onEdit(editedQuestion);
  }, [editedQuestion, onEdit]);

  React.useEffect(() => {
    const fetchAllBooks = async () => {
      if (!booksQuery.data) return;
      const booksData: Record<number, Book> = {};
      await Promise.all(
        booksQuery.data.map(async (book) => {
          try {
            const bookData = await healthFormsApi.books.get(book.id);
            booksData[book.id] = bookData;
          } catch (err) {
            console.error(`Failed to fetch book ${book.id}:`, err);
          }
        })
      );
      setAllBooksData(booksData);
    };
    fetchAllBooks();
  }, [booksQuery.data]);

  React.useEffect(() => {
    const fetchUsages = async () => {
      if (!editedQuestion || !booksQuery.data || !questionGroupsQuery.data) return;
      setIsLoadingUsages(true);
      try {
        const result = await findBooksAndGroupsUsingQuestion(
          editedQuestion.id,
          booksQuery.data,
          questionGroupsQuery.data,
          allBooksData
        );
        setUsages(result);
      } catch (err) {
        console.error("Failed to fetch usages:", err);
      } finally {
        setIsLoadingUsages(false);
      }
    };
    fetchUsages();
  }, [editedQuestion?.id, editedQuestion, booksQuery.data, questionGroupsQuery.data, allBooksData]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load question</Typography>;
  if (!editedQuestion) return null;

  const handleSave = () => {
    updateQuestion.mutate({ id, question: editedQuestion });
  };

  const handleChange = (field: keyof Question, value: unknown) => {
    setEditedQuestion((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleResponseTypeChange = (field: string, value: unknown) => {
    setEditedQuestion((prev) =>
      prev
        ? {
            ...prev,
            responseType: { ...prev.responseType, [field]: value } as QuestionResponseType,
          }
        : prev
    );
  };

  const handleResponseTypeSwitch = (newType: string) => {
    const defaults: Record<string, QuestionResponseType> = {
      select: { _type: "select", variants: [] },
      "select-or-other": { _type: "select-or-other", variants: [] },
      "multi-select": { _type: "multi-select", variants: [] },
      "multi-select-grouped": { _type: "multi-select-grouped", groups: [] },
      checkbox: { _type: "checkbox" },
      integer: { _type: "integer", min: 0, max: 100 },
      "number-range": { _type: "number-range", min: 0, max: 10 },
      vas: { _type: "vas", min: 0, max: 100 },
      date: { _type: "date" },
      text: { _type: "text", multiline: false },
    };

    setEditedQuestion((prev) =>
      prev
        ? {
            ...prev,
            responseType: defaults[newType] ?? prev.responseType,
          }
        : prev
    );
  };

  const handleDownload = () => {
    if (editedQuestion) {
      downloadJson(editedQuestion, `${editedQuestion.id}.json`);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this question?`)) {
      deleteQuestion.mutate(id);
    }
  };

  return (
    <div className={styles.editorContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
        <Tabs value={editorMode} onChange={(_, value) => setEditorMode(value)} indicatorColor="primary" textColor="primary">
          <Tab label="Structured Editor" value="structured" />
          <Tab label="✨" value="ai" />
        </Tabs>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {editorMode === "ai" ? (
        <div className={styles.form} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2} style={{ gap: 16 }}>
            <Typography variant="h5" style={{ opacity: 0.5 }}>✨ AI Chat Editor</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Coming soon: Chat with AI to edit your question
            </Typography>
          </Box>
          <Box p={2} borderTop="1px solid #e0e0e0" display="flex" style={{ gap: 8 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Ask me to modify the question..."
              disabled
            />
            <Button variant="contained" color="primary" disabled>
              Send
            </Button>
          </Box>
        </div>
      ) : (
        <div className={styles.form}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8, paddingTop: 8 }}>
            <Button
              size="small"
              style={{ color: "#f44336" }}
              onClick={handleDelete}
              disabled={deleteQuestion.isLoading}
            >
              {deleteQuestion.isLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={updateQuestion.isLoading}
            >
              {updateQuestion.isLoading ? "Saving..." : "Save"}
            </Button>
            <Tooltip title="Download JSON">
              <IconButton size="small" onClick={handleDownload}>
                <GetApp />
              </IconButton>
            </Tooltip>
          </Box>

          <TranslatedTextField
            label="Question Text"
            value={editedQuestion.question}
            onChange={(value) => handleChange("question", value)}
            multiline
            rows={2}
          />

          <TranslatedTextField
            label="Description"
            value={editedQuestion.description ?? { primary: "" }}
            onChange={(value) => handleChange("description", value.primary ? value : undefined)}
            multiline
            rows={2}
          />

      <TextField
        label="Semantic Key"
        value={editedQuestion.semanticKey ?? ""}
        onChange={(e) => handleChange("semanticKey", e.target.value)}
        fullWidth
        variant="outlined"
        margin="normal"
        error={!isValidSemanticKey(editedQuestion.semanticKey)}
        helperText={
          !isValidSemanticKey(editedQuestion.semanticKey)
            ? "Must start with a letter and contain only letters, numbers, and underscores"
            : "To allow special treatment programmatically"
        }
        FormHelperTextProps={{
          style: {
            color: !isValidSemanticKey(editedQuestion.semanticKey) ? "#f44336" : "rgba(0, 0, 0, 0.54)"
          }
        }}
      />

      <FormControl fullWidth variant="outlined" margin="normal">
        <InputLabel>Response Type</InputLabel>
        <Select
          value={editedQuestion.responseType._type}
          label="Response Type"
          onChange={(e) => handleResponseTypeSwitch(e.target.value as string)}
        >
          <MenuItem value="select">Select</MenuItem>
          <MenuItem value="select-or-other">Select or Other</MenuItem>
          <MenuItem value="multi-select">Multi-Select</MenuItem>
          <MenuItem value="multi-select-grouped">Multi-Select Grouped</MenuItem>
          <MenuItem value="checkbox">Checkbox</MenuItem>
          <MenuItem value="integer">Integer</MenuItem>
          <MenuItem value="number-range">Number Range</MenuItem>
          <MenuItem value="vas">VAS Slider</MenuItem>
          <MenuItem value="date">Date</MenuItem>
          <MenuItem value="text">Text</MenuItem>
        </Select>
      </FormControl>

      {editedQuestion.responseType._type === "multi-select-grouped" && (() => {
        const responseType = editedQuestion.responseType as { _type: "multi-select-grouped"; groups: Array<{ label: LocalizedText; variants: LocalizedText[] }> };
        return (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Groups
            </Typography>
            {responseType.groups.map((group, groupIdx) => (
              <Box key={groupIdx} mb={3} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                <Box display="flex" alignItems="flex-start" mb={2} style={{ gap: 8 }}>
                  <Box flex={1}>
                    <TranslatedTextField
                      label="Group Label"
                      value={group.label}
                      onChange={(value) => {
                        const groups = [...responseType.groups];
                        groups[groupIdx] = { ...groups[groupIdx], label: value };
                        handleResponseTypeChange("groups", groups);
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    style={{ color: "#f44336", marginTop: 24 }}
                    onClick={() => {
                      const groups = responseType.groups.filter((_: any, i: number) => i !== groupIdx);
                      handleResponseTypeChange("groups", groups);
                    }}
                  >
                    ✕
                  </Button>
                </Box>
                <Typography variant="caption" display="block" gutterBottom>
                  Variants
                </Typography>
                {group.variants.map((variant, variantIdx) => (
                  <Box key={variantIdx} display="flex" alignItems="flex-start" mb={1} style={{ gap: 8 }}>
                    <Box flex={1}>
                      <TranslatedTextField
                        label={`Variant ${variantIdx + 1}`}
                        value={variant}
                        onChange={(value) => {
                          const groups = [...responseType.groups];
                          const newVariants = [...groups[groupIdx].variants];
                          newVariants[variantIdx] = value;
                          groups[groupIdx] = { ...groups[groupIdx], variants: newVariants };
                          handleResponseTypeChange("groups", groups);
                        }}
                      />
                    </Box>
                    <Button
                      size="small"
                      style={{ color: "#f44336", marginTop: 24 }}
                      onClick={() => {
                        const groups = [...responseType.groups];
                        const newVariants = groups[groupIdx].variants.filter((_: LocalizedText, i: number) => i !== variantIdx);
                        groups[groupIdx] = { ...groups[groupIdx], variants: newVariants };
                        handleResponseTypeChange("groups", groups);
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => {
                    const groups = [...responseType.groups];
                    groups[groupIdx] = { ...groups[groupIdx], variants: [...groups[groupIdx].variants, { primary: "" }] };
                    handleResponseTypeChange("groups", groups);
                  }}
                >
                  Add Variant
                </Button>
              </Box>
            ))}
            <Button
              size="small"
              onClick={() => {
                const groups = [...responseType.groups, { label: { primary: "" }, variants: [] }];
                handleResponseTypeChange("groups", groups);
              }}
            >
              Add Group
            </Button>
          </Box>
        );
      })()}

      {(editedQuestion.responseType._type === "select" ||
        editedQuestion.responseType._type === "select-or-other" ||
        editedQuestion.responseType._type === "multi-select") && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Variants
          </Typography>
          {editedQuestion.responseType.variants.map((variant: LocalizedText, idx: number) => (
            <Box key={idx} display="flex" alignItems="flex-start" mb={1} style={{ gap: 8 }}>
              <Box flex={1}>
                <TranslatedTextField
                  label={`Variant ${idx + 1}`}
                  value={variant}
                  onChange={(value) => {
                    const variants = (editedQuestion.responseType as { variants: LocalizedText[] }).variants;
                    const newVariants = [...variants];
                    newVariants[idx] = value;
                    handleResponseTypeChange("variants", newVariants);
                  }}
                />
              </Box>
              <Button
                size="small"
                style={{ color: "#f44336", marginTop: 24 }}
                onClick={() => {
                  const variants = (editedQuestion.responseType as { variants: LocalizedText[] }).variants;
                  const newVariants = variants.filter((_: LocalizedText, i: number) => i !== idx);
                  handleResponseTypeChange("variants", newVariants);
                }}
              >
                ✕
              </Button>
            </Box>
          ))}
          <Button
            size="small"
            onClick={() => {
              const variants = (editedQuestion.responseType as { variants: LocalizedText[] }).variants;
              const newVariants = [...variants, { primary: "" }];
              handleResponseTypeChange("variants", newVariants);
            }}
          >
            Add Variant
          </Button>
        </Box>
      )}

      {(editedQuestion.responseType._type === "integer" || editedQuestion.responseType._type === "vas" || editedQuestion.responseType._type === "number-range") && (
        <Box display="flex" style={{ gap: 16 }}>
          <TextField
            label="Min"
            type="number"
            value={editedQuestion.responseType.min}
            onChange={(e) => handleResponseTypeChange("min", parseInt(e.target.value, 10))}
            variant="outlined"
            margin="normal"
            disabled={editedQuestion.responseType._type === "vas"}
          />
          <TextField
            label="Max"
            type="number"
            value={editedQuestion.responseType.max}
            onChange={(e) => handleResponseTypeChange("max", parseInt(e.target.value, 10))}
            variant="outlined"
            margin="normal"
            disabled={editedQuestion.responseType._type === "vas"}
          />
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary" display="block">
          ID: {editedQuestion.id}
        </Typography>
        {editedQuestion.hash && (
          <Typography variant="caption" color="textSecondary" display="block">
            Hash: {editedQuestion.hash.slice(0, 16)}...
          </Typography>
        )}
        {editedQuestion.updatedAt && (
          <Typography variant="caption" color="textSecondary" display="block">
            Updated: {new Date(editedQuestion.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Divider style={{ marginTop: 24, marginBottom: 16 }} />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Used in
        </Typography>
        {isLoadingUsages ? (
          <CircularProgress size={20} />
        ) : usages.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
            Not used in any books or question groups
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            {usages.map((usage) => (
              <Box
                key={`${usage.type}-${usage.id}`}
                className={styles.usageItem}
                onClick={() => {
                  const path = usage.type === "book" ? "books" : "question-groups";
                  history.push(`/health-forms/${path}/${usage.id}`);
                }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <span style={{ color: usage.isDirect ? "#28a745" : "#0066cc", fontSize: 16 }}>
                    {usage.isDirect ? "●" : "◎"}
                  </span>
                  <Typography variant="body2">{usage.name}</Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {usage.id}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {updateQuestion.isError && (
        <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
          Failed to save: {(updateQuestion.error as Error)?.message}
        </Typography>
      )}
      {updateQuestion.isSuccess && (
        <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
          Saved successfully
        </Typography>
      )}
        </div>
      )}
    </div>
  );
};

const BookEditor: React.VFC<{ id: number; isExpanded: boolean; onToggleExpand: () => void; onEdit: (entity: Book | null) => void }> = ({ id, isExpanded, onToggleExpand, onEdit }) => {
  const history = useHistory();
  const { data: book, isLoading, error } = useBook(id);
  const questionsQuery = useQuestions();
  const questionGroupsQuery = useQuestionGroups();
  const updateBook = useUpdateBook();
  const deleteBook = useDeleteBook();
  const [editedBook, setEditedBook] = React.useState<Book | null>(null);
  const [expandedVisibility, setExpandedVisibility] = React.useState<string | null>(null);
  const [expandedRequirement, setExpandedRequirement] = React.useState<string | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const lastPageRef = React.useRef<HTMLDivElement>(null);
  const [pageExpandedState, setPageExpandedState] = React.useState<Record<number, boolean>>({});
  const [editorMode, setEditorMode] = React.useState<"structured" | "ai">("structured");

  React.useEffect(() => {
    if (book) {
      setEditedBook(book);
      // Load page expanded states from localStorage
      const newState: Record<number, boolean> = {};
      book.pages.forEach((_, pageIdx) => {
        const key = `healthForms-page-expanded-${book.id}-${pageIdx}`;
        const stored = localStorage.getItem(key);
        newState[pageIdx] = stored === null ? true : stored === "true";
      });
      setPageExpandedState(newState);
    }
  }, [book]);

  React.useEffect(() => {
    onEdit(editedBook);
  }, [editedBook, onEdit]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load book</Typography>;
  if (!editedBook) return null;

  const handleSave = () => {
    updateBook.mutate({ id, book: editedBook });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Load initial values from localStorage
      let initialValues: InitialValues | undefined;
      const stored = localStorage.getItem("healthForms-initialValues");
      if (stored) {
        try {
          initialValues = JSON.parse(stored);
        } catch {
          initialValues = undefined;
        }
      }

      const resolved = await resolveBook(editedBook, initialValues);
      downloadJson(resolved, `${editedBook.id}-resolved.json`);
    } catch (err) {
      console.error("Failed to download resolved book:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const addPage = () => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPage: Page = { title: { primary: "" }, parts: [] };
      return { ...prev, pages: [...prev.pages, newPage] };
    });
    setTimeout(() => {
      lastPageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const removePage = (pageIdx: number) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      return { ...prev, pages: prev.pages.filter((_, i) => i !== pageIdx) };
    });
  };

  const movePage = (pageIdx: number, direction: "up" | "down") => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newIdx = direction === "up" ? pageIdx - 1 : pageIdx + 1;
      if (newIdx < 0 || newIdx >= prev.pages.length) return prev;
      const newPages = [...prev.pages];
      [newPages[pageIdx], newPages[newIdx]] = [newPages[newIdx], newPages[pageIdx]];
      return { ...prev, pages: newPages };
    });
  };

  const updatePageTitle = (pageIdx: number, title: LocalizedText | undefined) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      newPages[pageIdx] = { ...newPages[pageIdx], title };
      return { ...prev, pages: newPages };
    });
  };

  const addPartToPage = (pageIdx: number, partType: "question" | "question_group") => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newPart: Part =
        partType === "question"
          ? { _type: "question", questionId: 0, visibility: "visible", requirement: "optional" }
          : { _type: "question_group", questionGroup: 0, visibility: "visible" };
      newPages[pageIdx] = { ...newPages[pageIdx], parts: [...newPages[pageIdx].parts, newPart] };
      return { ...prev, pages: newPages };
    });
  };

  const removePartFromPage = (pageIdx: number, partIdx: number) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      newPages[pageIdx] = {
        ...newPages[pageIdx],
        parts: newPages[pageIdx].parts.filter((_, i) => i !== partIdx),
      };
      return { ...prev, pages: newPages };
    });
  };

  const movePartInPage = (pageIdx: number, partIdx: number, direction: "up" | "down") => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newIdx = direction === "up" ? partIdx - 1 : partIdx + 1;
      const page = prev.pages[pageIdx];
      if (newIdx < 0 || newIdx >= page.parts.length) return prev;
      const newPages = [...prev.pages];
      const newParts = [...page.parts];
      [newParts[partIdx], newParts[newIdx]] = [newParts[newIdx], newParts[partIdx]];
      newPages[pageIdx] = { ...page, parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const updatePartQuestion = (pageIdx: number, partIdx: number, questionId: number) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = { ...part, questionId };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const updatePartQuestionGroup = (pageIdx: number, partIdx: number, questionGroup: number) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question_group") {
        newParts[partIdx] = { ...part, questionGroup };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartVisibility = (pageIdx: number, partIdx: number, type: "visible" | "visibleWhen") => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      newParts[partIdx] = {
        ...newParts[partIdx],
        visibility: type === "visible" ? "visible" : { visibleWhen: "" },
      };
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartVisibilityPredicate = (pageIdx: number, partIdx: number, predicate: string) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      newParts[partIdx] = { ...newParts[partIdx], visibility: { visibleWhen: predicate } };
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartRequirement = (pageIdx: number, partIdx: number, type: "optional" | "required" | "requiredWhen") => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = {
          ...part,
          requirement: type === "requiredWhen" ? { requiredWhen: "" } : type,
        };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartRequirementPredicate = (pageIdx: number, partIdx: number, predicate: string) => {
    setEditedBook((prev) => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = { ...part, requirement: { requiredWhen: predicate } };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const getPartKey = (pageIdx: number, partIdx: number) => `${pageIdx}-${partIdx}`;

  const togglePageExpanded = (pageIdx: number) => {
    if (!editedBook) return;
    const newState = !(pageExpandedState[pageIdx] ?? true);
    setPageExpandedState((prev) => ({ ...prev, [pageIdx]: newState }));
    const key = `healthForms-page-expanded-${editedBook.id}-${pageIdx}`;
    localStorage.setItem(key, String(newState));
  };

  const isPageExpanded = (pageIdx: number) => pageExpandedState[pageIdx] ?? true;

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this book?`)) {
      deleteBook.mutate(id);
    }
  };

  return (
    <div className={styles.editorContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
        <Tabs value={editorMode} onChange={(_, value) => setEditorMode(value)} indicatorColor="primary" textColor="primary">
          <Tab label="Structured Editor" value="structured" />
          <Tab label="✨" value="ai" />
        </Tabs>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {editorMode === "ai" ? (
        <div className={styles.form} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2} style={{ gap: 16 }}>
            <Typography variant="h5" style={{ opacity: 0.5 }}>✨ AI Chat Editor</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Coming soon: Chat with AI to edit your book
            </Typography>
          </Box>
          <Box p={2} borderTop="1px solid #e0e0e0" display="flex" style={{ gap: 8 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Ask me to modify the book..."
              disabled
            />
            <Button variant="contained" color="primary" disabled>
              Send
            </Button>
          </Box>
        </div>
      ) : (
        <div className={styles.form}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8, paddingTop: 8 }}>
            <Button size="small" onClick={addPage}>
              Add Page
            </Button>
            <Button
              size="small"
              style={{ color: "#f44336" }}
              onClick={handleDelete}
              disabled={deleteBook.isLoading}
            >
              {deleteBook.isLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={updateBook.isLoading}
            >
              {updateBook.isLoading ? "Saving..." : "Save"}
            </Button>
            <Tooltip title="Download Resolved JSON">
              <IconButton size="small" onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <CircularProgress size={20} /> : <GetApp />}
              </IconButton>
            </Tooltip>
          </Box>

          <TextField
            label="Book Name"
          value={editedBook.name}
        onChange={(e) => setEditedBook((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
        fullWidth
        variant="outlined"
        margin="normal"
      />

      <TranslatedTextField
        label="Description (optional)"
        value={editedBook.description ?? { primary: "" }}
        onChange={(value) => setEditedBook((prev) => (prev ? { ...prev, description: value.primary ? value : undefined } : prev))}
        multiline
        rows={2}
      />

      <TranslatedTextField
        label="Footer (optional)"
        value={editedBook.footer ?? { primary: "" }}
        onChange={(value) => setEditedBook((prev) => (prev ? { ...prev, footer: value.primary ? value : undefined } : prev))}
        multiline
        rows={2}
      />

      {editedBook.pages.map((page, pageIdx) => (
        <div key={pageIdx} ref={pageIdx === editedBook.pages.length - 1 ? lastPageRef : undefined}>
        <Box className={styles.pageSection}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={isPageExpanded(pageIdx) ? 2 : 0}>
            <IconButton size="small" onClick={() => togglePageExpanded(pageIdx)}>
              {isPageExpanded(pageIdx) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Typography variant="subtitle2" style={{ whiteSpace: "nowrap" }}>
              Page {pageIdx + 1}{page.title?.primary ? ` (${page.title.primary})` : ""}
            </Typography>
            <Divider style={{ flex: 1 }} />
            <IconButton size="small" disabled={pageIdx === 0} onClick={() => movePage(pageIdx, "up")}>
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={pageIdx === editedBook.pages.length - 1}
              onClick={() => movePage(pageIdx, "down")}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
            <Button size="small" style={{ color: "#f44336" }} onClick={() => removePage(pageIdx)}>
              ✕
            </Button>
          </Box>

          {isPageExpanded(pageIdx) && (
            <>
              <Box mb={2}>
                <TranslatedTextField
                  label="Page Title"
                  value={page.title ?? { primary: "" }}
                  onChange={(value) => updatePageTitle(pageIdx, value.primary ? value : undefined)}
                />
              </Box>

              <Box display="flex" style={{ gap: 8 }} mb={2}>
                <Button size="small" variant="outlined" onClick={() => addPartToPage(pageIdx, "question")}>
                  Add Question
                </Button>
                <Button size="small" variant="outlined" onClick={() => addPartToPage(pageIdx, "question_group")}>
                  Add Question Group
                </Button>
              </Box>

              {page.parts.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                  No parts yet
                </Typography>
              )}

              {page.parts.map((part, partIdx) => {
            const isQuestion = part._type === "question";
            const bgColor = isQuestion ? "#d1ecf1" : "#e2d9f3";
            const isVisibilityConditional = typeof part.visibility === "object";
            const isRequirementConditional = isQuestion && typeof part.requirement === "object";
            const partKey = getPartKey(pageIdx, partIdx);

            return (
              <Box key={partIdx} mb={1}>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8, padding: 8, background: bgColor, borderRadius: 4 }}
                >
                  <IconButton size="small" disabled={partIdx === 0} onClick={() => movePartInPage(pageIdx, partIdx, "up")}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={partIdx === page.parts.length - 1}
                    onClick={() => movePartInPage(pageIdx, partIdx, "down")}
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>

                  {isQuestion ? (
                    <>
                      <Box display="flex" flexDirection="column" style={{ flex: 1, gap: 4, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8, width: "100%" }}>
                          <FormControl size="small" style={{ flex: 1, minWidth: 0 }}>
                            <Select
                              value={questionsQuery.data?.some((q) => q.id === part.questionId) ? part.questionId : ""}
                              onChange={(e) => updatePartQuestion(pageIdx, partIdx, Number(e.target.value))}
                              displayEmpty
                              variant="outlined"
                            >
                              <MenuItem value="">-- Select a question --</MenuItem>
                              {questionsQuery.data?.map((q) => (
                                <MenuItem key={q.id} value={q.id}>
                                  {q.question.primary.length > 50 ? q.question.primary.slice(0, 50) + "..." : q.question.primary}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {part.questionId && (
                            <Tooltip title="Open question">
                              <IconButton
                                size="small"
                                onClick={() => history.push(`/health-forms/questions/${part.questionId}`)}
                                style={{ padding: 4, flexShrink: 0 }}
                              >
                                <ArrowForward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {part.questionId && (() => {
                          const selectedQuestion = questionsQuery.data?.find((q) => String(q.id) === String(part.questionId));
                          return selectedQuestion?.semanticKey ? (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ paddingLeft: 8, cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => {
                                navigator.clipboard.writeText(selectedQuestion.semanticKey!);
                              }}
                              title="Click to copy"
                            >
                              {selectedQuestion.semanticKey}
                            </Typography>
                          ) : null;
                        })()}
                      </Box>
                    </>
                  ) : (
                    <>
                      <FormControl size="small" style={{ flex: 1 }}>
                        <Select
                          value={questionGroupsQuery.data?.some((qg) => qg.id === part.questionGroup) ? part.questionGroup : ""}
                          onChange={(e) => updatePartQuestionGroup(pageIdx, partIdx, Number(e.target.value))}
                          displayEmpty
                          variant="outlined"
                        >
                          <MenuItem value="">-- Select a question group --</MenuItem>
                          {questionGroupsQuery.data?.map((qg) => (
                            <MenuItem key={qg.id} value={qg.id}>
                              {qg.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {part.questionGroup && (
                        <Tooltip title="Open question group">
                          <IconButton
                            size="small"
                            onClick={() => history.push(`/health-forms/question-groups/${part.questionGroup}`)}
                            style={{ padding: 4 }}
                          >
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}

                  <Button
                    size="small"
                    onClick={() => setExpandedVisibility(expandedVisibility === partKey ? null : partKey)}
                    style={{
                      background: isVisibilityConditional ? "#ffc107" : "#28a745",
                      color: isVisibilityConditional ? "#000" : "#fff",
                    }}
                  >
                    👁️
                  </Button>

                  {isQuestion && (
                    <Button
                      size="small"
                      onClick={() => setExpandedRequirement(expandedRequirement === partKey ? null : partKey)}
                      style={{
                        background: isRequirementConditional
                          ? "#ffc107"
                          : part.requirement === "required"
                          ? "#1976d2"
                          : "#6c757d",
                        color: isRequirementConditional ? "#000" : "#fff",
                      }}
                    >
                      ⚙️
                    </Button>
                  )}

                  <Button size="small" style={{ color: "#f44336" }} onClick={() => removePartFromPage(pageIdx, partIdx)}>
                    ✕
                  </Button>
                </Box>

                {expandedVisibility === partKey && (
                  <Box className={styles.expandedSection}>
                    <Typography variant="caption" gutterBottom display="block">
                      Visibility
                    </Typography>
                    <Box display="flex" style={{ gap: 8 }} mb={1}>
                      <Button
                        size="small"
                        variant={part.visibility === "visible" ? "contained" : "outlined"}
                        onClick={() => setPartVisibility(pageIdx, partIdx, "visible")}
                      >
                        Visible
                      </Button>
                      <Button
                        size="small"
                        variant={isVisibilityConditional ? "contained" : "outlined"}
                        onClick={() => setPartVisibility(pageIdx, partIdx, "visibleWhen")}
                      >
                        Visible When
                      </Button>
                    </Box>
                    {isVisibilityConditional && (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="FEEL predicate"
                        value={(part.visibility as { visibleWhen: string }).visibleWhen}
                        onChange={(e) => setPartVisibilityPredicate(pageIdx, partIdx, e.target.value)}
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}

                {expandedRequirement === partKey && isQuestion && (
                  <Box className={styles.expandedSectionAlt}>
                    <Typography variant="caption" gutterBottom display="block">
                      Requirement
                    </Typography>
                    <Box display="flex" style={{ gap: 8 }} mb={1}>
                      <Button
                        size="small"
                        variant={part.requirement === "optional" ? "contained" : "outlined"}
                        onClick={() => setPartRequirement(pageIdx, partIdx, "optional")}
                      >
                        Optional
                      </Button>
                      <Button
                        size="small"
                        variant={part.requirement === "required" ? "contained" : "outlined"}
                        onClick={() => setPartRequirement(pageIdx, partIdx, "required")}
                      >
                        Required
                      </Button>
                      <Button
                        size="small"
                        variant={isRequirementConditional ? "contained" : "outlined"}
                        onClick={() => setPartRequirement(pageIdx, partIdx, "requiredWhen")}
                      >
                        Required When
                      </Button>
                    </Box>
                    {isRequirementConditional && (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="FEEL predicate"
                        value={(part.requirement as { requiredWhen: string }).requiredWhen}
                        onChange={(e) => setPartRequirementPredicate(pageIdx, partIdx, e.target.value)}
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
            </>
          )}
        </Box>
        </div>
      ))}

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary" display="block">
          ID: {editedBook.id}
        </Typography>
        {editedBook.updatedAt && (
          <Typography variant="caption" color="textSecondary" display="block">
            Updated: {new Date(editedBook.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      {updateBook.isError && (
        <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
          Failed to save: {(updateBook.error as Error)?.message}
        </Typography>
      )}
      {updateBook.isSuccess && (
        <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
          Saved successfully
        </Typography>
      )}
        </div>
      )}
    </div>
  );
};

const QuestionGroupEditor: React.VFC<{ id: number; isExpanded: boolean; onToggleExpand: () => void; onEdit: (entity: QuestionGroup | null) => void }> = ({ id, isExpanded, onToggleExpand, onEdit }) => {
  const history = useHistory();
  const { data: group, isLoading, error } = useQuestionGroup(id);
  const questionsQuery = useQuestions();
  const booksQuery = useBooks();
  const updateGroup = useUpdateQuestionGroup();
  const deleteGroup = useDeleteQuestionGroup();
  const [editedGroup, setEditedGroup] = React.useState<QuestionGroup | null>(null);
  const [expandedVisibility, setExpandedVisibility] = React.useState<number | null>(null);
  const [expandedRequirement, setExpandedRequirement] = React.useState<number | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [allBooksData, setAllBooksData] = React.useState<Record<number, Book>>({});
  const [usages, setUsages] = React.useState<UsageItem[]>([]);
  const [isLoadingUsages, setIsLoadingUsages] = React.useState(false);
  const [editorMode, setEditorMode] = React.useState<"structured" | "ai">("structured");

  React.useEffect(() => {
    if (group) {
      setEditedGroup(group);
    }
  }, [group]);

  React.useEffect(() => {
    onEdit(editedGroup);
  }, [editedGroup, onEdit]);

  React.useEffect(() => {
    const fetchAllBooks = async () => {
      if (!booksQuery.data) return;
      const booksData: Record<number, Book> = {};
      await Promise.all(
        booksQuery.data.map(async (book) => {
          try {
            const bookData = await healthFormsApi.books.get(book.id);
            booksData[book.id] = bookData;
          } catch (err) {
            console.error(`Failed to fetch book ${book.id}:`, err);
          }
        })
      );
      setAllBooksData(booksData);
    };
    fetchAllBooks();
  }, [booksQuery.data]);

  React.useEffect(() => {
    const fetchUsages = async () => {
      if (!editedGroup || !booksQuery.data) return;
      setIsLoadingUsages(true);
      try {
        const result = findBooksUsingQuestionGroup(editedGroup.id, booksQuery.data, allBooksData);
        setUsages(result);
      } catch (err) {
        console.error("Failed to fetch usages:", err);
      } finally {
        setIsLoadingUsages(false);
      }
    };
    fetchUsages();
  }, [editedGroup?.id, editedGroup, booksQuery.data, allBooksData]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load question group</Typography>;
  if (!editedGroup) return null;

  const handleSave = () => {
    updateGroup.mutate({ id, group: editedGroup });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Load initial values from localStorage
      let initialValues: InitialValues | undefined;
      const stored = localStorage.getItem("healthForms-initialValues");
      if (stored) {
        try {
          initialValues = JSON.parse(stored);
        } catch {
          initialValues = undefined;
        }
      }

      const resolved = await resolveQuestionGroup(editedGroup, initialValues);
      downloadJson(resolved, `${editedGroup.id}-resolved.json`);
    } catch (err) {
      console.error("Failed to download resolved question group:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const updateQuestion = (idx: number, questionId: number) => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = { ...newQuestions[idx], questionId };
      return { ...prev, questions: newQuestions };
    });
  };

  const moveQuestion = (idx: number, direction: "up" | "down") => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.questions.length) return prev;
      const newQuestions = [...prev.questions];
      [newQuestions[idx], newQuestions[newIdx]] = [newQuestions[newIdx], newQuestions[idx]];
      return { ...prev, questions: newQuestions };
    });
  };

  const removeQuestion = (idx: number) => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== idx) };
    });
  };

  const addQuestion = () => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newRef: QuestionRef = {
        questionId: 0,
        visibility: "visible",
        requirement: "optional",
      };
      return { ...prev, questions: [...prev.questions, newRef] };
    });
  };

  const setVisibility = (idx: number, type: "visible" | "visibleWhen") => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        visibility: type === "visible" ? "visible" : { visibleWhen: "" },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setVisibilityPredicate = (idx: number, predicate: string) => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        visibility: { visibleWhen: predicate },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setRequirement = (idx: number, type: "optional" | "required" | "requiredWhen") => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        requirement: type === "requiredWhen" ? { requiredWhen: "" } : type,
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setRequirementPredicate = (idx: number, predicate: string) => {
    setEditedGroup((prev) => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        requirement: { requiredWhen: predicate },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this question group?`)) {
      deleteGroup.mutate(id);
    }
  };

  return (
    <div className={styles.editorContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
        <Tabs value={editorMode} onChange={(_, value) => setEditorMode(value)} indicatorColor="primary" textColor="primary">
          <Tab label="Structured Editor" value="structured" />
          <Tab label="✨" value="ai" />
        </Tabs>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {editorMode === "ai" ? (
        <div className={styles.form} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2} style={{ gap: 16 }}>
            <Typography variant="h5" style={{ opacity: 0.5 }}>✨ AI Chat Editor</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Coming soon: Chat with AI to edit your question group
            </Typography>
          </Box>
          <Box p={2} borderTop="1px solid #e0e0e0" display="flex" style={{ gap: 8 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Ask me to modify the question group..."
              disabled
            />
            <Button variant="contained" color="primary" disabled>
              Send
            </Button>
          </Box>
        </div>
      ) : (
        <div className={styles.form}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8, paddingTop: 8 }}>
            <Button size="small" onClick={addQuestion}>
              Add Question
            </Button>
            <Button
              size="small"
              style={{ color: "#f44336" }}
              onClick={handleDelete}
              disabled={deleteGroup.isLoading}
            >
              {deleteGroup.isLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={updateGroup.isLoading}
            >
              {updateGroup.isLoading ? "Saving..." : "Save"}
            </Button>
            <Tooltip title="Download Resolved JSON">
              <IconButton size="small" onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <CircularProgress size={20} /> : <GetApp />}
              </IconButton>
            </Tooltip>
          </Box>

          <TextField
            label="Name"
          value={editedGroup.name}
        onChange={(e) => setEditedGroup((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
        fullWidth
        variant="outlined"
        margin="normal"
      />

      <TranslatedTextField
        label="Description"
        value={editedGroup.description ?? { primary: "" }}
        onChange={(value) => setEditedGroup((prev) => (prev ? { ...prev, description: value.primary ? value : undefined } : prev))}
        multiline
        rows={2}
      />

      <Box mt={3} mb={2}>
        <Typography variant="subtitle2">Questions ({editedGroup.questions.length})</Typography>
      </Box>

      {editedGroup.questions.map((qRef, idx) => {
        const isVisibilityConditional = typeof qRef.visibility === "object";
        const isRequirementConditional = typeof qRef.requirement === "object";

        return (
          <Box key={idx} className={styles.questionRefCard}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <IconButton size="small" disabled={idx === 0} onClick={() => moveQuestion(idx, "up")}>
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={idx === editedGroup.questions.length - 1}
                onClick={() => moveQuestion(idx, "down")}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>

              <Box display="flex" flexDirection="column" style={{ flex: 1, gap: 4, minWidth: 0 }}>
                <Box display="flex" alignItems="center" style={{ gap: 8, width: "100%" }}>
                  <FormControl size="small" style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={questionsQuery.data?.some((q) => q.id === qRef.questionId) ? qRef.questionId : ""}
                      onChange={(e) => updateQuestion(idx, Number(e.target.value))}
                      displayEmpty
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select a question --</MenuItem>
                      {questionsQuery.data?.map((q) => (
                        <MenuItem key={q.id} value={q.id}>
                          {q.question.primary.length > 50 ? q.question.primary.slice(0, 50) + "..." : q.question.primary}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {qRef.questionId && (
                    <Tooltip title="Open question">
                      <IconButton
                        size="small"
                        onClick={() => history.push(`/health-forms/questions/${qRef.questionId}`)}
                        style={{ padding: 4, flexShrink: 0 }}
                      >
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {qRef.questionId && (() => {
                  const selectedQuestion = questionsQuery.data?.find((q) => String(q.id) === String(qRef.questionId));
                  return selectedQuestion?.semanticKey ? (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ paddingLeft: 8, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        navigator.clipboard.writeText(selectedQuestion.semanticKey!);
                      }}
                      title="Click to copy"
                    >
                      {selectedQuestion.semanticKey}
                    </Typography>
                  ) : null;
                })()}
              </Box>

              <Button
                size="small"
                onClick={() => setExpandedVisibility(expandedVisibility === idx ? null : idx)}
                style={{
                  background: isVisibilityConditional ? "#ffc107" : "#28a745",
                  color: isVisibilityConditional ? "#000" : "#fff",
                }}
              >
                👁️
              </Button>

              <Button
                size="small"
                onClick={() => setExpandedRequirement(expandedRequirement === idx ? null : idx)}
                style={{
                  background: isRequirementConditional
                    ? "#ffc107"
                    : qRef.requirement === "required"
                    ? "#1976d2"
                    : "#6c757d",
                  color: isRequirementConditional ? "#000" : "#fff",
                }}
              >
                ⚙️
              </Button>

              <Button size="small" style={{ color: "#f44336" }} onClick={() => removeQuestion(idx)}>
                ✕
              </Button>
            </Box>

            {expandedVisibility === idx && (
              <Box className={styles.expandedSection}>
                <Typography variant="caption" gutterBottom display="block">
                  Visibility
                </Typography>
                <Box display="flex" style={{ gap: 8 }} mb={1}>
                  <Button
                    size="small"
                    variant={qRef.visibility === "visible" ? "contained" : "outlined"}
                    onClick={() => setVisibility(idx, "visible")}
                  >
                    Visible
                  </Button>
                  <Button
                    size="small"
                    variant={isVisibilityConditional ? "contained" : "outlined"}
                    onClick={() => setVisibility(idx, "visibleWhen")}
                  >
                    Visible When
                  </Button>
                </Box>
                {isVisibilityConditional && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="FEEL predicate"
                    value={(qRef.visibility as { visibleWhen: string }).visibleWhen}
                    onChange={(e) => setVisibilityPredicate(idx, e.target.value)}
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            {expandedRequirement === idx && (
              <Box className={styles.expandedSectionAlt}>
                <Typography variant="caption" gutterBottom display="block">
                  Requirement
                </Typography>
                <Box display="flex" style={{ gap: 8 }} mb={1}>
                  <Button
                    size="small"
                    variant={qRef.requirement === "optional" ? "contained" : "outlined"}
                    onClick={() => setRequirement(idx, "optional")}
                  >
                    Optional
                  </Button>
                  <Button
                    size="small"
                    variant={qRef.requirement === "required" ? "contained" : "outlined"}
                    onClick={() => setRequirement(idx, "required")}
                  >
                    Required
                  </Button>
                  <Button
                    size="small"
                    variant={isRequirementConditional ? "contained" : "outlined"}
                    onClick={() => setRequirement(idx, "requiredWhen")}
                  >
                    Required When
                  </Button>
                </Box>
                {isRequirementConditional && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="FEEL predicate"
                    value={(qRef.requirement as { requiredWhen: string }).requiredWhen}
                    onChange={(e) => setRequirementPredicate(idx, e.target.value)}
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })}

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary" display="block">
          ID: {editedGroup.id}
        </Typography>
        {editedGroup.updatedAt && (
          <Typography variant="caption" color="textSecondary" display="block">
            Updated: {new Date(editedGroup.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Divider style={{ marginTop: 24, marginBottom: 16 }} />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Used in
        </Typography>
        {isLoadingUsages ? (
          <CircularProgress size={20} />
        ) : usages.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
            Not used in any books
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            {usages.map((usage) => (
              <Box
                key={`${usage.type}-${usage.id}`}
                className={styles.usageItem}
                onClick={() => {
                  history.push(`/health-forms/books/${usage.id}`);
                }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <span style={{ color: "#28a745", fontSize: 16 }}>●</span>
                  <Typography variant="body2">{usage.name}</Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {usage.id}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {updateGroup.isError && (
        <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
          Failed to save: {(updateGroup.error as Error)?.message}
        </Typography>
      )}
      {updateGroup.isSuccess && (
        <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
          Saved successfully
        </Typography>
      )}
        </div>
      )}
    </div>
  );
};

const Editor: React.VFC<EditorProps> = ({ selection, isExpanded, onToggleExpand, onEdit }) => {
  React.useEffect(() => {
    if (!selection) {
      onEdit(null);
    }
  }, [selection, onEdit]);

  if (!selection) {
    return (
      <div className={styles.empty}>
        <Typography color="textSecondary">Select an item to edit</Typography>
      </div>
    );
  }

  if (selection.type === "question") {
    return <QuestionEditor id={selection.id} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onEdit={onEdit} />;
  }

  if (selection.type === "book") {
    return <BookEditor id={selection.id} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onEdit={onEdit} />;
  }

  if (selection.type === "questionGroup") {
    return <QuestionGroupEditor id={selection.id} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onEdit={onEdit} />;
  }

  return null;
};

export default Editor;

const styles = {
  editorContainer: css`
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  form: css`
    padding: 16px;
    overflow-y: auto;
    flex: 1;
  `,
  empty: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  `,
  pagePreview: css`
    padding: 8px;
    margin: 4px 0;
    background: #f5f5f5;
    border-radius: 4px;
  `,
  questionRef: css`
    padding: 8px;
    margin: 4px 0;
    background: #f5f5f5;
    border-radius: 4px;
  `,
  questionRefCard: css`
    margin-bottom: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    padding: 8px;
  `,
  expandedSection: css`
    padding: 12px;
    margin-top: 8px;
    border-top: 1px solid #ddd;
    background: #f8f9fa;
  `,
  expandedSectionAlt: css`
    padding: 12px;
    margin-top: 8px;
    border-top: 1px solid #ddd;
    background: #f8f9fa;
  `,
  pageSection: css`
    padding: 16px;
    margin-bottom: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;
  `,
  usageItem: css`
    padding: 12px;
    background: #f8f9fa;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &:hover {
      background: #e9ecef;
    }
  `,
};
